import { useState } from "react";
import { toast } from "sonner";
import { OctagonX, Power, ShieldAlert } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export function EmergencyButton({ onDone }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);
  const fire = async () => {
    setBusy(true);
    try {
      const r = await api.post("/emergency/stop", { password: "", note: "dashboard kill switch" });
      setReport(r.data);
      toast.error("EMERGENCY STOP executed — engine locked");
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <>
      <button data-testid="emergency-stop-button" onClick={() => { setReport(null); setOpen(true); }}
        className="pulse-red flex items-center gap-2 bg-[#DC2626] hover:bg-[#B91C1C] text-white font-mono text-xs sm:text-sm font-bold uppercase tracking-wider px-3 sm:px-4 py-2 rounded-md transition-colors">
        <OctagonX className="w-4 h-4" /> <span className="hidden sm:inline">Emergency</span> Stop
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="emergency-stop-dialog" className="bg-[#12141D] border-red-900 max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-red-400 flex items-center gap-2"><ShieldAlert className="w-5 h-5" /> Emergency Stop</DialogTitle>
            <DialogDescription className="text-zinc-400">
              Immediately locks the trading engine, cancels pending entry orders, and closes open positions with reduce-only execution. Only you can reactivate.
            </DialogDescription>
          </DialogHeader>
          {report ? (
            <div data-testid="emergency-report" className="space-y-1.5 max-h-64 overflow-auto">
              {report.steps.map((s, i) => (
                <div key={i} className={`font-mono text-xs ${s.ok ? "text-emerald-400" : "text-red-400"}`}>{s.ok ? "✓" : "✗"} {s.step} {s.detail && <span className="text-zinc-500">— {s.detail}</span>}</div>
              ))}
            </div>
          ) : null}
          <DialogFooter>
            {!report ? (
              <Button data-testid="emergency-stop-confirm" disabled={busy} onClick={fire} className="w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white font-mono uppercase tracking-wider font-bold h-12">
                {busy ? "Executing…" : "Execute emergency stop now"}
              </Button>
            ) : (
              <Button data-testid="emergency-close" variant="secondary" onClick={() => setOpen(false)} className="w-full">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function LockedBanner({ status, onDone }) {
  const [open, setOpen] = useState(false);
  const [pw, setPw] = useState("");
  const [txt, setTxt] = useState("");
  const [busy, setBusy] = useState(false);
  if (!status?.locked) return null;
  const go = async () => {
    setBusy(true);
    try {
      await api.post("/emergency/reactivate", { password: pw, confirm_text: txt });
      toast.success("Engine reactivated");
      setOpen(false);
      setPw(""); setTxt("");
      onDone?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setBusy(false);
  };
  return (
    <div data-testid="locked-banner" className="stripes bg-[#450A0A] border border-red-800 rounded-lg px-4 py-3 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
      <div>
        <div className="font-mono text-sm font-bold text-red-300 uppercase tracking-wider">Trading engine locked — all new entries blocked</div>
        <div className="text-xs text-red-200/70 mt-0.5">{status.lock_reason}</div>
      </div>
      <Button data-testid="reactivate-open-button" onClick={() => setOpen(true)} className="bg-zinc-100 text-zinc-900 hover:bg-white font-mono text-xs uppercase tracking-wider">
        <Power className="w-4 h-4 mr-1" /> Reactivate
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="reactivate-dialog" className="bg-[#12141D] border-zinc-800">
          <DialogHeader>
            <DialogTitle className="font-display">Manual reactivation</DialogTitle>
            <DialogDescription>No AI agent can do this. Confirm with your password and type REACTIVATE.</DialogDescription>
          </DialogHeader>
          <Input data-testid="reactivate-password-input" type="password" placeholder="Owner password" value={pw} onChange={(e) => setPw(e.target.value)} />
          <Input data-testid="reactivate-confirm-input" placeholder="Type REACTIVATE" value={txt} onChange={(e) => setTxt(e.target.value)} className="font-mono" />
          <DialogFooter>
            <Button data-testid="reactivate-submit-button" disabled={busy || txt !== "REACTIVATE" || !pw} onClick={go} className="w-full">Reactivate engine</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
